import { Button } from 'shared/ui/Button';
import { SideModal, SideModalBody, SideModalFooter } from 'shared/ui/SideModal';
import { TextareaMD } from 'shared/ui/Textarea/TextareaMD';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useCreateCampaignNote, useGetCampaignNotes } from 'entities/campaign';
import { Note } from 'shared/ui/Note';
import { Skeleton } from 'shared/ui/Skeleton';
import { FormErrors } from 'shared/ui/FormErrors';
import classnames from 'classnames';

const DISABLED_STYLES = 'pointer-events-none opacity-60';

export interface ShowNotesModalProps {
  campaignId: number;
}

export function ShowNotesModal({ campaignId }: ShowNotesModalProps) {
  const methods = useForm<{ note: string }>({ defaultValues: { note: '' } });
  const { handleSubmit, control, reset } = methods;
  const noteText = useWatch({ control, name: 'note' });
  const createNoteQuery = useCreateCampaignNote(campaignId);
  const getNotesQuery = useGetCampaignNotes(campaignId);
  const notesData = getNotesQuery.data?.data || [];

  const onSubmit = async () => {
    createNoteQuery.mutate(
      {
        payload: {
          note: noteText,
        },
      },
      {
        onSuccess: () => reset(),
      }
    );
  };

  const getNotesList = () => {
    const list = notesData.map((item) => {
      return (
        <div
          key={item.createdAt}
          className="mb-4"
        >
          {getNotesQuery.isLoading ? <Skeleton className="h-25 w-full" /> : <Note {...item} />}
        </div>
      );
    });

    return <div>{list}</div>;
  };

  return (
    <SideModal autoTestAttributesName="notes-modal">
      <SideModalBody
        title="Notes"
        isLarge
        className="px-[76px]"
      >
        {getNotesList()}
      </SideModalBody>
      <SideModalFooter>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={classnames('flex flex-col gap-[6px]', createNoteQuery.isLoading && DISABLED_STYLES)}
          >
            <TextareaMD
              placeholder="Notes"
              name="note"
              className="w-[340px] flex-1"
              isDisabled={createNoteQuery.isLoading}
            />
            <Button
              type="submit"
              disabled={noteText.trim().length === 0}
              loading={createNoteQuery.isLoading}
            >
              Add note
            </Button>
          </form>
          <FormErrors error={createNoteQuery.error} />
        </FormProvider>
      </SideModalFooter>
    </SideModal>
  );
}
